import {type EventMap, Listenable, type Notification} from "@juis/commons";
import type {EntityClass, EntityValues, ModelFields} from "./Model.js";
import type {FieldMap} from "./types.js";

export interface EntityEvents extends EventMap {
    change: Notification<{ changed: readonly string[] }>;
    delete: Notification;
}

/**
 * One canonical instance per model and id. Views listen for `change` instead of re-reading,
 * so a refreshed payload updates every place the entity is shown.
 *
 * Applications never extend this directly: `defineModel` returns the base class of one model,
 * and its instances are that model's entities.
 */
export class Entity<F extends FieldMap> extends Listenable<EntityEvents> {
    #values: Partial<EntityValues<ModelFields<F>>>;

    constructor(values: Partial<EntityValues<ModelFields<F>>>) {
        super();
        this.#values = values;
    }

    /** The class this entity belongs to, carrying the fields and the transport. */
    get model(): EntityClass<F> {
        return this.constructor as unknown as EntityClass<F>;
    }

    get id(): number {
        const id = this.#values.id;
        if (typeof id !== "number") {
            throw new Error(`${this.model.modelName} entity has no id`);
        }
        return id;
    }

    get values(): Readonly<Partial<EntityValues<ModelFields<F>>>> {
        return this.#values;
    }

    get<K extends keyof ModelFields<F> & string>(name: K): EntityValues<ModelFields<F>>[K] | null {
        const value = this.#values[name];
        return value === undefined ? null : value;
    }

    /** Reads a value by path, for filters and table columns that address fields dynamically. */
    read(path: string): unknown {
        return (this.#values as Record<string, unknown>)[path];
    }

    /** Writes changed values and refreshes this instance from the response. */
    async update(values: Partial<EntityValues<ModelFields<F>>>): Promise<this> {
        const model = this.model;
        const envelope = await model.client.patch(`/${model.path}/${this.id}`, model.serialize(values));
        this.refresh(model.parse(envelope.data));
        return this;
    }

    async delete(): Promise<void> {
        const model = this.model;
        const id = this.id;
        await model.client.delete(`/${model.path}/${id}`);
        model.forget(id);
        this.trigger("delete", undefined);
    }

    /** Merges a newer payload in place and reports which fields actually changed. */
    refresh(values: Partial<EntityValues<ModelFields<F>>>): readonly string[] {
        const changed: string[] = [];
        for (const [key, value] of Object.entries(values)) {
            const current = (this.#values as Record<string, unknown>)[key];
            if (!isSameValue(current, value)) {
                changed.push(key);
            }
        }
        this.#values = {...this.#values, ...values};
        if (changed.length > 0) {
            this.trigger("change", {changed});
        }
        return changed;
    }
}

const isSameValue = (current: unknown, next: unknown): boolean => {
    if (current instanceof Date && next instanceof Date) {
        return current.getTime() === next.getTime();
    }
    if (Array.isArray(current) && Array.isArray(next)) {
        return current.length === next.length && current.every((item, index) => isSameValue(item, next[index]));
    }
    if (isIdentified(current) && isIdentified(next)) {
        return current.id === next.id;
    }
    return current === next;
};

const isIdentified = (value: unknown): value is { id: unknown } =>
    typeof value === "object" && value !== null && "id" in value;
